// Parse GooseBerry markdown output into a structured payload that we can render in tabs.

const CONFIDENCE_RE = /\[Confidence:\s*(Low|Medium|High)\s*\]/i;
const H2_RE = /^##\s+(.+?)\s*$/;

// Matches a persona line such as:
// **Sander (52, Utrecht)**, arbeidsdeskundige...
// **Sander (52, Utrecht)** - arbeidsdeskundige...
// Sander (52, Utrecht): arbeidsdeskundige...
// Matches require a parenthesized profile so ordinary bold sentences
// are not mistaken for persona names.
const USER_LINE_RE = /^\s*(?:[-*]\s+|\d+\.\s+)?(?:\*\*([^*]+?\([^)]{1,80}\)[^*]*?)\*\*|([\p{Lu}][\p{L}\s-]{1,50}\s*\([^)]+\)))\s*[\u2014\-:,]\s*(.*)$/u;
const BOLD_PERSONA_RE = /\*\*([^*]{2,90}\([^)]{1,80}\)[^*]{0,20})\*\*\s*[\u2014\-:,]?\s*/gu;

function _capFirst(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
}

function _normalizeKey(title) {
  return title.toLowerCase().replace(/\s+/g, " ").trim();
}

function _finalizeSection(section) {
  section.body = (section.bodyLines || []).join("\n").trim();
  delete section.bodyLines;
  return section;
}

function _parseHeading(line) {
  const h2 = H2_RE.exec(line);
  if (!h2) return null;
  const headingFull = h2[1];
  const confMatch = CONFIDENCE_RE.exec(headingFull);
  const confidence = confMatch ? _capFirst(confMatch[1]) : null;
  const title = headingFull.replace(CONFIDENCE_RE, "").trim();
  return { title, key: _normalizeKey(title), confidence, body: "", bodyLines: [] };
}

function _extractSections(lines) {
  const sections = [];
  let current = null;
  for (const line of lines) {
    const heading = _parseHeading(line);
    if (heading) {
      if (current) sections.push(_finalizeSection(current));
      current = heading;
      continue;
    }
    if (current) current.bodyLines.push(line);
  }
  if (current) sections.push(_finalizeSection(current));
  return sections;
}

function _indexByKey(sections) {
  const out = {};
  for (const s of sections) {
    if (!out[s.key]) out[s.key] = s;
  }
  return out;
}

function _extractOverallConfidence(bySection) {
  const overall = bySection["overall confidence"];
  if (!overall) return null;
  const m = /\b(Low|Medium|High)\b/i.exec(overall.body);
  return m ? _capFirst(m[1]) : null;
}

function _extractUsers(text) {
  if (!text) return [];
  const fromBoldHeaders = _extractUsersFromBoldHeaders(text);
  if (fromBoldHeaders.length > 0) return fromBoldHeaders;

  const blocks = [];
  let buf = null;
  for (const line of text.split("\n")) {
    const m = USER_LINE_RE.exec(line);
    if (m) {
      if (buf) blocks.push(buf);
      // m[1] = bold name, m[2] = plain name, m[3] = rest of line after separator
      const rawName = (m[1] || m[2] || "").trim();
      const restOfLine = (m[3] || "").trim();
      // Put the rest of the line as first body line so single-line profiles work
      buf = { name: rawName, bodyLines: restOfLine ? [restOfLine] : [] };
    } else if (buf) {
      buf.bodyLines.push(line);
    }
  }
  if (buf) blocks.push(buf);

  const out = [];
  for (const b of blocks) {
    const body = b.bodyLines.join("\n").trim();
    if (b.name && b.name.length < 80) {
      const cleanName = b.name.replace(/\s*\([^)]*\)/, "").replace(/:$/, "").trim();
      if (!out.find((u) => u.name === cleanName)) {
        const profile = b.name.match(/\(([^)]+)\)/)?.[1] || null;
        out.push({ name: cleanName, profile, body });
      }
    }
  }
  return out;
}

function _extractUsersFromBoldHeaders(text) {
  const matches = [...text.matchAll(BOLD_PERSONA_RE)];
  if (matches.length === 0) return [];

  const out = [];
  for (let i = 0; i < matches.length; i += 1) {
    const match = matches[i];
    const rawName = (match[1] || "").trim();
    const bodyStart = match.index + match[0].length;
    const bodyEnd = i + 1 < matches.length ? matches[i + 1].index : text.length;
    const body = text
      .slice(bodyStart, bodyEnd)
      .replace(/^\s*[\u2014\-:,]\s*/, "")
      .trim();
    const cleanName = rawName.replace(/\s*\([^)]*\)/, "").replace(/:$/, "").trim();
    if (cleanName && cleanName.length < 80 && !out.find((u) => u.name === cleanName)) {
      const profile = rawName.match(/\(([^)]+)\)/)?.[1] || null;
      out.push({ name: cleanName, profile, body });
    }
  }
  return out;
}

function _extractUsersFromSections(bySection) {
  const syn = bySection["synthetic users"];
  const fromSyn = syn ? _extractUsers(syn.body) : [];
  if (fromSyn.length > 0) return fromSyn;
  const dlg = bySection["dialogue"];
  return dlg ? _extractUsers(dlg.body) : [];
}

export function parseGooseBerryMarkdown(md) {
  const result = {
    sections: [],
    bySection: {},
    overallConfidence: null,
    syntheticUsers: [],
    raw: md || "",
  };
  if (!md) return result;

  const sections = _extractSections(md.split("\n"));
  const bySection = _indexByKey(sections);

  result.sections = sections;
  result.bySection = bySection;
  result.overallConfidence = _extractOverallConfidence(bySection);
  result.syntheticUsers = _extractUsersFromSections(bySection);
  return result;
}
